/**
 * Phase 14a-3 — admin dashboard schemas.
 *
 * `MetricDefinitionResponseSchema` is the catalog shape returned by
 * `GET /admin/dashboard/metrics` — the admin Configure UI (14c) and the
 * v1 dashboard both read from this list.
 *
 * `MetricResultSchema` is the per-metric computed value returned by
 * `GET /admin/dashboard?metrics=...` — drives card rendering. `status`
 * derived from each metric's locked thresholds; `trend` reserved (undefined
 * at v1) for v1.x.
 *
 * Convention 06 — admin namespace gets its own folder under
 * `packages/schemas/src/admin/`. Admin-specific shapes don't fit any
 * existing resource namespace; they cross-cut several (dashboard reads
 * vetting + shifts + billing + notifications + payouts).
 */
import { z } from 'zod';
/**
 * Categories the dashboard groups by. v1 surfaces 5 metrics matching the
 * 14-G operational mix. New metrics in 14c+ land under one of these
 * categories or a new one is added here.
 */
export const MetricCategorySchema = z.enum([
    'vetting',
    'shifts',
    'billing',
    'payouts',
    'notifications',
]);
/** Status the card paints (border + tone) per the operator's mental model. */
export const MetricStatusSchema = z.enum(['ok', 'warning', 'critical']);
/** Per-metric definition — catalog row returned to admin UI. */
export const MetricDefinitionResponseSchema = z.object({
    /** Stable id used as the storage key in admin_preference.dashboardMetricIds. */
    id: z.string().min(1),
    /** Card title shown to the operator. */
    label: z.string().min(1),
    /** Section the dashboard groups the card under. */
    category: MetricCategorySchema,
    /** One-sentence operator-facing description ("Caregivers awaiting vetting decisions"). */
    description: z.string().min(1),
    /** Click-through path inside the admin app; UI renders the card as a link if set. */
    drilldownPath: z.string().optional(),
    /** Optional locked thresholds — surface in the catalog so the UI can show them. */
    thresholds: z
        .object({
        warning: z.number().optional(),
        critical: z.number().optional(),
    })
        .optional(),
});
/** Catalog endpoint shape: a list of `MetricDefinitionResponse`. */
export const MetricCatalogResponseSchema = z.object({
    items: z.array(MetricDefinitionResponseSchema),
});
/** Per-metric computed result. Value is a number for counts or a string for
 *  the aging summary "current/1-30/31-60/60+" buckets — kept loose so future
 *  composite metrics (e.g. percentile) can ship without a schema bump. */
export const MetricResultSchema = z.object({
    value: z.union([z.number(), z.string()]),
    status: MetricStatusSchema,
    /** Reserved for v1.x — v1 returns undefined. */
    trend: z
        .object({
        delta: z.number(),
        period: z.enum(['7d', '30d']),
    })
        .optional(),
});
/** Query for `GET /admin/dashboard?metrics=m1,m2,m3`. */
export const DashboardComputeQuerySchema = z.object({
    metrics: z
        .string()
        .min(1)
        .transform((s) => s.split(',').map((p) => p.trim()).filter(Boolean))
        .pipe(z.array(z.string().min(1)).min(1).max(20)),
});
/** Compute endpoint shape: keyed by metric id. */
export const DashboardComputeResponseSchema = z.object({
    results: z.record(z.string(), MetricResultSchema),
});
// Phase 14a-3 AdminPreference* schemas migrated to
// `packages/schemas/src/operator/preferences.schema.ts` at Phase 14c
// (renamed OperatorPreferenceResponseSchema + OperatorPreferencePatchRequestSchema).
// URL surface moved from /admin/me/preferences to /me/operator/preferences.
//# sourceMappingURL=dashboard.schema.js.map